import React, { useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  Chip,
  Switch,
  Button,
  Dialog,
  DialogContent,
  Divider,
  Collapse,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Close as CloseIcon,
  AddPhotoAlternate as AddPhotoIcon,
  ImageOutlined as ImageIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import ImagenesAIService from '../../services/imagenesAI';
import ImagenesService from '../../services/imagenes';

const ACCENT = '#673ab7';
const ACCENT_LIGHT = '#ede7f6';
const ACCENT_BG = '#f8f5ff';

const VariantCard = ({
  variant,
  index,
  onRemove,
  onChange,
  onChangeSizeDetails,
  onAddSize,
  onRemoveSize,
  onAddImage,
  onRemoveImage,
}) => {
  const [newSize, setNewSize] = useState('');
  const [uploadingImages, setUploadingImages] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [imagePrompt, setImagePrompt] = useState('');

  const totalStock =
    variant.sizes && variant.sizes.length > 0
      ? (variant.size_details || []).reduce((acc, sd) => acc + (sd.quantity || 0), 0)
      : variant.quantity || 0;

  const handleAddSizeClick = () => {
    if (newSize.trim()) {
      onAddSize(index, newSize.trim());
      setNewSize('');
    }
  };

  const handleOpenPreview = (imgIndex) => {
    setCurrentImageIndex(imgIndex);
    setPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setCurrentImageIndex(0);
  };

  const handlePrevImage = () => {
    setCurrentImageIndex((prev) => 
      prev === 0 ? variant.images.length - 1 : prev - 1
    );
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) => 
      prev === variant.images.length - 1 ? 0 : prev + 1
    );
  };

  const handleImagesSelected = useCallback(
    async (files) => {
      setUploadingImages(true);
      try {
        const formData = new FormData();
        files.forEach((file) => formData.append('image', file));
        let uploadedUrls;
        if (imagePrompt.trim()) {
          formData.append('prompt', imagePrompt.trim());
          uploadedUrls = await ImagenesAIService.generate(formData);
        } else {
          uploadedUrls = await ImagenesService.upload(formData);
        }
        uploadedUrls.forEach((url) => onAddImage(index, url));
      } catch (error) {
        console.error('Error al subir imágenes:', error);
      } finally {
        setUploadingImages(false);
      }
    },
    [index, onAddImage, imagePrompt]
  );

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderLeft: `4px solid ${ACCENT}`,
        overflow: 'hidden',
        transition: 'box-shadow 0.2s',
        '&:hover': { boxShadow: 2 },
      }}
    >
      {/* ── Header (always visible, click to expand) ─────── */}
      <Box
        onClick={() => setExpanded((e) => !e)}
        sx={{
          px: { xs: 1.5, sm: 2 },
          py: 1.2,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          cursor: 'pointer',
          userSelect: 'none',
          '&:hover': { backgroundColor: 'rgba(103,58,183,0.04)' },
        }}
      >
        {/* Number badge */}
        <Box
          sx={{
            width: 22,
            height: 22,
            borderRadius: '50%',
            backgroundColor: ACCENT_LIGHT,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Typography
            sx={{ color: ACCENT, fontWeight: 800, fontSize: '0.65rem', lineHeight: 1 }}
          >
            {index + 1}
          </Typography>
        </Box>

        {/* Color dot (only when there's a color value) */}
        {variant.color && (
          <Box
            sx={{
              width: 14,
              height: 14,
              borderRadius: '50%',
              backgroundColor: variant.color,
              border: `2px solid ${ACCENT_LIGHT}`,
              flexShrink: 0,
            }}
          />
        )}

        {/* Name + summary */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: '0.88rem',
              color: variant.color ? 'text.primary' : 'text.disabled',
              textTransform: 'capitalize',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {variant.color || 'Sin nombre'}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
            {totalStock} u.
            {variant.sizes?.length > 0 && ` · ${variant.sizes.length} tallas`}
            {variant.images?.length > 0 && ` · ${variant.images.length} img`}
          </Typography>
        </Box>

        {/* Availability chip (desktop) */}
        <Chip
          label={variant.available !== false ? 'Disp.' : 'No disp.'}
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onChange(index, 'available', !(variant.available !== false));
          }}
          sx={{
            height: 22,
            fontSize: '0.65rem',
            fontWeight: 700,
            backgroundColor: variant.available !== false ? '#e8f5e9' : '#ffebee',
            color: variant.available !== false ? '#2e7d32' : '#c62828',
            cursor: 'pointer',
            flexShrink: 0,
            display: { xs: 'none', sm: 'flex' },
          }}
        />

        {/* Switch (mobile) */}
        <Switch
          checked={variant.available !== false}
          onChange={(e) => {
            e.stopPropagation();
            onChange(index, 'available', e.target.checked);
          }}
          size="small"
          onClick={(e) => e.stopPropagation()}
          sx={{ display: { xs: 'flex', sm: 'none' }, mx: 0 }}
        />

        {/* Delete */}
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onRemove(index);
          }}
          sx={{
            color: 'text.secondary',
            flexShrink: 0,
            '&:hover': { color: 'error.main', backgroundColor: '#ffebee' },
          }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>

        {/* Expand icon */}
        {expanded ? (
          <ExpandLessIcon sx={{ color: ACCENT, fontSize: 20, flexShrink: 0 }} />
        ) : (
          <ExpandMoreIcon sx={{ color: ACCENT, fontSize: 20, flexShrink: 0 }} />
        )}
      </Box>

      {/* ── Body (collapsible) ───────────────────────────── */}
      <Collapse in={expanded}>
        <Divider />
        <Box sx={{ p: { xs: 1.5, sm: 2 } }}>

          {/* Color + Cantidad */}
          <Box
            sx={{
              display: 'flex',
              gap: 1.5,
              mb: 2,
              flexDirection: { xs: 'column', sm: 'row' },
            }}
          >
            <TextField
              label="Nombre del color"
              placeholder="ej: Rojo, Azul Marino"
              value={variant.color || ''}
              onChange={(e) => onChange(index, 'color', e.target.value)}
              size="small"
              fullWidth
            />
            <TextField
              label="Cantidad"
              type="number"
              value={
                variant.sizes && variant.sizes.length > 0
                  ? (variant.size_details || []).reduce(
                      (acc, sd) => acc + (sd.quantity || 0),
                      0
                    )
                  : variant.quantity || 0
              }
              onChange={
                variant.sizes && variant.sizes.length > 0
                  ? undefined
                  : (e) => onChange(index, 'quantity', parseInt(e.target.value) || 0)
              }
              size="small"
              inputProps={{
                min: 0,
                readOnly: !!(variant.sizes && variant.sizes.length > 0),
              }}
              helperText={
                variant.sizes && variant.sizes.length > 0 ? 'Suma de talles' : undefined
              }
              sx={{ minWidth: { sm: 130 } }}
            />
          </Box>

          {/* ── Tallas ─────────────────────────────────────── */}
          <Typography
            variant="caption"
            sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.8 }}
          >
            Tallas disponibles
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6, mb: 1, minHeight: 28 }}>
            {variant.sizes && variant.sizes.length > 0 ? (
              variant.sizes.map((size, sizeIdx) => (
                <Chip
                  key={sizeIdx}
                  label={size}
                  size="small"
                  onDelete={() => onRemoveSize(index, size)}
                  sx={{
                    height: 26,
                    fontSize: '0.72rem',
                    fontWeight: 600,
                    backgroundColor: ACCENT_LIGHT,
                    color: '#4527a0',
                    '& .MuiChip-deleteIcon': { color: ACCENT, fontSize: 14 },
                    '& .MuiChip-deleteIcon:hover': { color: '#4527a0' },
                  }}
                />
              ))
            ) : (
              <Typography variant="caption" color="text.disabled" fontStyle="italic">
                Sin tallas — agregá abajo
              </Typography>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField
              size="small"
              placeholder="ej: S, M, L, XL, 38, 40"
              value={newSize}
              onChange={(e) => setNewSize(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleAddSizeClick()}
              sx={{ flex: 1 }}
            />
            <Button
              size="small"
              variant="outlined"
              onClick={handleAddSizeClick}
              disabled={!newSize.trim()}
              sx={{
                borderColor: ACCENT,
                color: ACCENT,
                textTransform: 'none',
                whiteSpace: 'nowrap',
                fontWeight: 600,
                '&:hover': { backgroundColor: ACCENT_BG, borderColor: ACCENT },
              }}
            >
              + Talla
            </Button>
          </Box>

          {/* ── Stock por talle ──────────────────────────── */}
          {variant.sizes && variant.sizes.length > 0 && (
            <Box
              sx={{
                mb: 2,
                p: 1.5,
                borderRadius: 1.5,
                backgroundColor: ACCENT_BG,
                border: `1px solid ${ACCENT_LIGHT}`,
              }}
            >
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, color: ACCENT, display: 'block', mb: 1 }}
              >
                Stock por talle
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {variant.sizes.map((size, i) => {
                  const sizeDetail =
                    (variant.size_details || []).find((sd) => sd.size === size) ||
                    { size, quantity: 0, available: false };
                  return (
                    <Box
                      key={i}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.8,
                        flex: '1 0 140px',
                        minWidth: 0,
                      }}
                    >
                      <Chip
                        label={size}
                        size="small"
                        sx={{
                          width: 44,
                          height: 22,
                          fontSize: '0.68rem',
                          fontWeight: 700,
                          backgroundColor:
                            sizeDetail.quantity > 0 ? ACCENT_LIGHT : 'action.hover',
                          color:
                            sizeDetail.quantity > 0 ? '#4527a0' : 'text.secondary',
                        }}
                      />
                      <TextField
                        type="number"
                        size="small"
                        value={sizeDetail.quantity}
                        onChange={(e) => {
                          const qty = Math.max(0, parseInt(e.target.value) || 0);
                          const newSizeDetails = variant.sizes.map((s) => {
                            const existing =
                              (variant.size_details || []).find(
                                (sd) => sd.size === s
                              ) || { size: s, quantity: 0, available: false };
                            return s === size
                              ? { ...existing, quantity: qty, available: qty > 0 }
                              : existing;
                          });
                          const totalQty = newSizeDetails.reduce(
                            (acc, sd) => acc + sd.quantity,
                            0
                          );
                          onChangeSizeDetails(index, newSizeDetails, totalQty);
                        }}
                        inputProps={{ min: 0 }}
                        sx={{ flex: 1, maxWidth: 90 }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          color: sizeDetail.quantity > 0 ? '#2e7d32' : '#c62828',
                          fontWeight: 700,
                          fontSize: '0.68rem',
                          minWidth: 32,
                        }}
                      >
                        {sizeDetail.quantity > 0 ? `${sizeDetail.quantity} u.` : 'Sin'}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

          {/* ── Imágenes ─────────────────────────────────── */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1 }}>
              <ImageIcon sx={{ fontSize: 14, color: ACCENT }} />
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                Imágenes
              </Typography>
              {variant.images?.length > 0 && (
                <Chip
                  label={variant.images.length}
                  size="small"
                  sx={{
                    height: 16,
                    fontSize: '0.6rem',
                    fontWeight: 700,
                    backgroundColor: ACCENT_LIGHT,
                    color: '#4527a0',
                  }}
                />
              )}
            </Box>

            {/* Horizontal scroll row: thumbnails + upload button */}
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                overflowX: 'auto',
                pb: 0.5,
                alignItems: 'center',
                '&::-webkit-scrollbar': { height: 4 },
                '&::-webkit-scrollbar-thumb': {
                  borderRadius: 2,
                  backgroundColor: ACCENT_LIGHT,
                },
              }}
            >
              {variant.images &&
                variant.images.map((img, imgIdx) => (
                  <Box key={imgIdx} sx={{ position: 'relative', flexShrink: 0 }}>
                    <Box
                      component="img"
                      src={img}
                      alt={`Img ${imgIdx + 1}`}
                      onClick={() => handleOpenPreview(imgIdx)}
                      sx={{
                        width: 56,
                        height: 56,
                        objectFit: 'cover',
                        borderRadius: 1.5,
                        cursor: 'pointer',
                        border: `2px solid ${ACCENT_LIGHT}`,
                        display: 'block',
                        transition: 'border-color 0.2s, transform 0.15s',
                        '&:hover': { borderColor: ACCENT, transform: 'scale(1.04)' },
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemoveImage(index, img);
                      }}
                      sx={{
                        position: 'absolute',
                        top: -6,
                        right: -6,
                        width: 18,
                        height: 18,
                        minWidth: 0,
                        p: 0,
                        backgroundColor: 'error.main',
                        color: 'white',
                        borderRadius: '50%',
                        '&:hover': { backgroundColor: 'error.dark' },
                        '& .MuiSvgIcon-root': { fontSize: 11 },
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                  </Box>
                ))}

              {/* Upload tile — matches thumbnail size */}
              <Box sx={{ flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <TextField
                  size='small'
                  placeholder='Prompt IA (opcional)'
                  value={imagePrompt}
                  onChange={(e) => setImagePrompt(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  sx={{ width: 130, '& .MuiInputBase-input': { fontSize: '0.68rem', py: 0.5 } }}
                />
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  style={{ display: 'none' }}
                  id={`image-upload-variant-${index}`}
                  onChange={async (e) => {
                    const files = Array.from(e.target.files);
                    if (files.length > 0) {
                      await handleImagesSelected(files);
                      e.target.value = '';
                    }
                  }}
                />
                <label htmlFor={`image-upload-variant-${index}`}>
                  <Box
                    component="span"
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 56,
                      height: 56,
                      border: `1.5px dashed rgba(103,58,183,0.35)`,
                      borderRadius: 1.5,
                      cursor: 'pointer',
                      backgroundColor: 'rgba(103,58,183,0.03)',
                      transition: 'all 0.2s',
                      '&:hover': { backgroundColor: ACCENT_BG, borderColor: ACCENT },
                    }}
                  >
                    <AddPhotoIcon sx={{ fontSize: 20, color: ACCENT, opacity: 0.7 }} />
                    <Typography
                      variant="caption"
                      sx={{ fontSize: '0.55rem', color: ACCENT, lineHeight: 1, mt: 0.3 }}
                    >
                      {uploadingImages ? '...' : 'Subir'}
                    </Typography>
                  </Box>
                </label>
              </Box>
            </Box>
          </Box>
        </Box>
      </Collapse>

      {/* ── Modal carrusel ───────────────────────────────────── */}
      <Dialog
        open={previewOpen}
        onClose={handleClosePreview}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { backgroundColor: '#000', color: '#fff' } }}
      >
        <DialogContent sx={{ p: 0, position: 'relative', minHeight: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <IconButton
            onClick={handleClosePreview}
            sx={{ position: 'absolute', top: 8, right: 8, color: '#fff', backgroundColor: 'rgba(0,0,0,0.5)', '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' } }}
          >
            <CloseIcon />
          </IconButton>

          {variant.images && variant.images.length > 1 && (
            <IconButton
              onClick={handlePrevImage}
              sx={{ position: 'absolute', left: 8, color: '#fff', backgroundColor: 'rgba(0,0,0,0.5)', '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' } }}
            >
              <ChevronLeftIcon fontSize="large" />
            </IconButton>
          )}

          {variant.images && variant.images[currentImageIndex] && (
            <Box sx={{ textAlign: 'center', width: '100%' }}>
              <img
                src={variant.images[currentImageIndex]}
                alt={`Preview ${currentImageIndex + 1}`}
                style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
              />
              {variant.images.length > 1 && (
                <Typography
                  variant="caption"
                  sx={{
                    position: 'absolute', bottom: 16, left: '50%', transform: 'translateX(-50%)',
                    backgroundColor: 'rgba(0,0,0,0.7)', padding: '4px 12px', borderRadius: 1,
                  }}
                >
                  {currentImageIndex + 1} / {variant.images.length}
                </Typography>
              )}
            </Box>
          )}

          {variant.images && variant.images.length > 1 && (
            <IconButton
              onClick={handleNextImage}
              sx={{ position: 'absolute', right: 8, color: '#fff', backgroundColor: 'rgba(0,0,0,0.5)', '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' } }}
            >
              <ChevronRightIcon fontSize="large" />
            </IconButton>
          )}
        </DialogContent>
      </Dialog>
    </Paper>
  );
};

export default VariantCard;
